import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  CheckCircle2,
  Clock,
  CreditCard,
  Send,
  ClipboardList,
  Plane,
  Briefcase,
  Users,
  CalendarClock,
  UserCheck,
  AlertTriangle,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFab } from "@/components/site/WhatsAppFab";
import { ReviewsSection } from "@/components/site/ReviewsSection";
import { useConfig, whatsappLink } from "@/context/ConfigContext";
import { formatNaira } from "@/lib/format";
import { track } from "@/lib/analytics";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const NigeriaFlag = ({ className = "h-3.5 w-5" }) => (
  <svg viewBox="0 0 6 3" className={className} aria-hidden="true">
    <rect width="6" height="3" fill="#ffffff" />
    <rect width="2" height="3" fill="#008751" />
    <rect x="4" width="2" height="3" fill="#008751" />
  </svg>
);

const STEPS = [
  { n: "01", icon: ClipboardList, title: "Tell us about your trip", body: "Submit your passenger details, destination and intended travel dates." },
  { n: "02", icon: FileText, title: "Choose your service", body: "Select Standard, Express or Priority processing." },
  { n: "03", icon: CreditCard, title: "Make your payment", body: "Pay securely through Paystack." },
  { n: "04", icon: Send, title: "Receive your reservation", body: "Your confirmed airline reservation is delivered electronically within the selected processing time." },
];

const RECEIVE = [
  "Full passenger name as provided",
  "Departure and return travel dates",
  "Flight itinerary",
  "Airline information",
  "PNR / reservation reference",
  "Confirmed reservation status",
  "Electronic itinerary",
  "One change included",
];

const AUDIENCE = [
  { icon: Plane, label: "Tourist visa applicants" },
  { icon: Briefcase, label: "Business visa applicants" },
  { icon: Users, label: "Family visit applicants" },
  { icon: CalendarClock, label: "Conference / event travellers" },
  { icon: UserCheck, label: "Other applicants needing travel documentation" },
];

const IMPORTANT = [
  "VisaFlight provides a confirmed airline reservation for visa application documentation. It is not a purchased airline ticket.",
  "The reservation's validity is determined by the airline and reservation system.",
  "VisaFlight does not guarantee visa approval.",
  "Visa requirements vary by country, embassy, consulate and visa category. Customers should confirm the current documentation requirements for their specific application before ordering.",
  "Customers are responsible for providing accurate passenger and travel information.",
  "Processing times begin only after successful payment and receipt of all required information.",
  "One itinerary change is included. Additional changes are ₦10,000 each.",
];

const FAQS = [
  { q: "Is this a flight ticket?", a: "No. VisaFlight provides a confirmed airline reservation for visa application purposes. It is not a purchased airline ticket." },
  { q: "What do I receive?", a: "You receive an electronic airline reservation itinerary containing your passenger name, travel dates, flight details, PNR/reservation reference and confirmed reservation status." },
  { q: "How long does it take?", a: "Standard orders are delivered within 24 hours. Express orders are delivered within 4 hours. Priority orders are delivered within 1 hour. Processing begins after successful payment and receipt of all required information." },
  { q: "Can I change my reservation?", a: "Yes. One change is included. Additional changes after the included change cost ₦10,000 each." },
  { q: "Do you guarantee visa approval?", a: "No. VisaFlight only provides the requested travel reservation. Visa decisions are made by the relevant embassy, consulate or visa authority." },
  { q: "Does every embassy accept this?", a: "Visa documentation requirements vary. Please confirm the current requirements of the embassy, consulate or visa application centre handling your application." },
  { q: "Can I buy my flight later?", a: "Yes. This service is specifically for obtaining a reservation rather than purchasing the ticket upfront. Customers should make their final travel arrangements according to their visa outcome and travel plans." },
  { q: "What if I enter the wrong name or dates?", a: "Customers are responsible for providing accurate information. Please review your details carefully before payment. One change is included after the reservation is created." },
];

export default function Landing() {
  const config = useConfig();
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, []);

  const order = (service, location) => {
    track("order_now_click", { location, service });
    navigate(service ? `/order?service=${service}` : "/order");
  };

  return (
    <div className="min-h-screen bg-background pb-20 sm:pb-0">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="vf-grid absolute inset-0 opacity-60" />
        <div className="relative mx-auto grid max-w-6xl gap-12 px-5 py-16 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
              <NigeriaFlag className="h-3 w-5 rounded-[1px] ring-1 ring-border" /> Made in Nigeria · For Nigerian visa applicants
            </span>
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Confirmed Airline Reservations for Visa Applications
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Get a genuine confirmed airline reservation for your visa application without purchasing your flight ticket
              upfront.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                data-testid="hero-get-reservation"
                onClick={() => order(null, "hero")}
                size="lg"
                className="rounded-sm bg-primary px-8 text-base font-semibold text-primary-foreground hover:bg-[#003DB3]"
              >
                Get My Reservation
              </Button>
              <a
                href={whatsappLink(config)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp"
                onClick={() => track("whatsapp_click", { location: "hero" })}
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-border bg-card px-8 py-3 text-base font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
              >
                <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
              </a>
            </div>
            <p className="mt-6 text-sm font-medium text-muted-foreground">
              {formatNaira(40000)} standard service • One change included • Delivered within 24 hours
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><NigeriaFlag className="h-2.5 w-4 rounded-[1px] ring-1 ring-border" /> Priced in Naira (₦)</span>
              <span>• Paystack secure checkout</span>
              <span>• Local WhatsApp support</span>
            </div>
          </motion.div>

          {/* Reservation preview card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="rounded-sm border border-border bg-card shadow-sm">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <span className="font-heading text-sm font-bold tracking-wide text-foreground">RESERVATION ITINERARY</span>
                <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#0F4C3A]/10 px-2.5 py-1 text-xs font-semibold text-[#0F4C3A]">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
                </span>
              </div>
              <div className="space-y-4 px-5 py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Passenger</p>
                    <p className="font-heading text-base font-semibold text-foreground">A. ADEYEMI</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">PNR</p>
                    <p className="font-mono text-base font-semibold text-foreground">X7K2QP</p>
                  </div>
                </div>
                <div className="flex items-center justify-between border-y border-dashed border-border py-4">
                  <div>
                    <p className="font-heading text-2xl font-bold text-foreground">LOS</p>
                    <p className="text-xs text-muted-foreground">Lagos</p>
                  </div>
                  <Plane className="h-5 w-5 text-primary" />
                  <div className="text-right">
                    <p className="font-heading text-2xl font-bold text-foreground">LHR</p>
                    <p className="text-xs text-muted-foreground">London</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Depart</p>
                    <p className="font-medium text-foreground">14 Aug 2026</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Return</p>
                    <p className="font-medium text-foreground">28 Aug 2026</p>
                  </div>
                </div>
                <p className="pt-2 text-xs leading-relaxed text-muted-foreground">
                  Sample preview. This is a reservation for documentation — not a purchased airline ticket.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-b border-border py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">How It Works</h2>
          <p className="mt-3 max-w-xl text-muted-foreground">Four simple steps from your details to a confirmed reservation.</p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className="rounded-sm border border-border bg-card p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading text-3xl font-extrabold text-border">{s.n}</span>
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 inline-flex items-center gap-2 rounded-sm border border-border bg-secondary px-4 py-3 text-sm font-medium text-foreground">
            <CheckCircle2 className="h-4 w-4 text-primary" /> One itinerary change is included with every reservation.
          </div>

          <div className="mt-12">
            <h3 className="font-heading text-lg font-semibold text-foreground">Watch a quick walkthrough</h3>
            <p className="mt-1 text-sm text-muted-foreground">See exactly how to place your order, step by step.</p>
            <div className="mt-5 overflow-hidden rounded-sm border border-border bg-card shadow-sm">
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  src="https://pamelas-workspace.portal.trainn.co/share/6Fpr0m9vr2w5V3LXIQ7GAg/embed?autoplay=false"
                  title="How VisaFlight works — step-by-step walkthrough"
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  data-testid="how-it-works-video"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="border-b border-border bg-card py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Your confirmed reservation includes
          </h2>
          <div className="mt-10 grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {RECEIVE.map((r) => (
              <div key={r} className="flex items-start gap-3 border-b border-border pb-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0F4C3A]" />
                <span className="text-base text-foreground">{r}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm font-medium text-muted-foreground">
            This is a reservation, not a purchased airline ticket.
          </p>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-border py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Simple, transparent pricing</h2>
          <p className="mt-3 max-w-xl text-muted-foreground">Choose the processing speed that fits your timeline.</p>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {config.services.map((svc, i) => {
              const featured = false;
              return (
                <motion.div
                  key={svc.id}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={fadeUp}
                  className={`flex flex-col rounded-sm border bg-card p-8 ${
                    featured ? "border-primary shadow-sm ring-1 ring-primary" : "border-border"
                  }`}
                >
                  {featured && (
                    <span className="mb-4 inline-block w-fit rounded-sm bg-primary px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
                      Most popular
                    </span>
                  )}
                  <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">{svc.name}</h3>
                  <p className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-foreground">
                    {formatNaira(svc.amount)}
                  </p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                    <Clock className="h-4 w-4" /> {svc.processing_time}
                  </p>
                  <ul className="mt-6 flex-1 space-y-3 text-sm text-foreground">
                    {(svc.id === "standard"
                      ? ["Confirmed airline reservation", "PNR / reservation reference", "Full passenger details", "Travel dates and itinerary", "One change included"]
                      : ["Everything in Standard", "Confirmed airline reservation", "PNR / reservation reference", "Full passenger details", "One change included"]
                    ).map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C3A]" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`pricing-${svc.id}-btn`}
                    onClick={() => order(svc.id, "pricing")}
                    className={`mt-8 rounded-sm font-semibold ${
                      featured
                        ? "bg-primary text-primary-foreground hover:bg-[#003DB3]"
                        : "bg-foreground text-background hover:bg-foreground/90"
                    }`}
                  >
                    Choose {svc.name}
                  </Button>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-10 space-y-3">
            <p className="rounded-sm border border-border bg-secondary px-4 py-3 text-sm text-foreground">
              Processing time begins after successful payment and after all required information has been received.
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Additional changes:</span> After your included change,
              additional itinerary changes cost {formatNaira(config.additional_change_fee)} each.
            </p>
          </div>
        </div>
      </section>

      {/* WHO IT IS FOR */}
      <section className="border-b border-border bg-card py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Designed for visa applicants in Nigeria</h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            VisaFlight is built for people in Nigeria applying for visas who have been asked to provide proof of intended
            travel, including an airline reservation or itinerary.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCE.map((a) => (
              <div key={a.label} className="flex items-center gap-4 rounded-sm border border-border p-5 transition-colors hover:border-primary">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary">
                  <a.icon className="h-5 w-5" />
                </div>
                <span className="text-base font-medium text-foreground">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMPORTANT INFORMATION */}
      <section className="border-b border-border py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="rounded-sm border-l-4 border-primary bg-card p-8 shadow-sm ring-1 ring-border">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-primary" />
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">Please read before ordering</h2>
            </div>
            <ul className="mt-6 space-y-4">
              {IMPORTANT.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-border bg-card py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading text-base font-semibold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <ReviewsSection />

      {/* WHATSAPP CTA */}
      <section className="bg-primary py-16">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-primary-foreground">
            Have a question before ordering?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/85">
            Chat with us on WhatsApp and we'll help you get the right reservation for your visa application.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={whatsappLink(config)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
              onClick={() => track("whatsapp_click", { location: "cta-section" })}
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#25D366] px-8 py-3 text-base font-semibold text-white transition-transform hover:scale-[1.02]"
            >
              <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
            </a>
            <Button
              data-testid="cta-order"
              onClick={() => order(null, "cta-section")}
              size="lg"
              className="rounded-sm bg-background px-8 text-base font-semibold text-foreground hover:bg-background/90"
            >
              Get My Reservation
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
